/**
 * User-interaction tracing (opt-in, Kapitalvins parity) — the zone.js-bearing
 * module.
 *
 * `@opentelemetry/instrumentation-user-interaction` turns DOM interactions
 * (click / submit / keydown / change / dblclick) into spans, and
 * `@opentelemetry/context-zone`'s {@link ZoneContextManager} keeps the trace
 * context across the async work those interactions kick off (so a fetch fired from
 * a click is a child of the interaction span).
 *
 * BOTH pull in zone.js, which monkey-patches the host's global timers / `Promise`
 * / `addEventListener` AT IMPORT. To honour the SDK's "never disturb the host
 * unless asked" contract, this module is imported ONLY via a dynamic `import()`
 * from {@link import('./otel-rum.js')} when `userInteraction` is enabled — so
 * zone.js never loads (and the bundle never grows) for consumers who don't opt in.
 */
import { ZoneContextManager } from '@opentelemetry/context-zone'
import { registerInstrumentations, type Instrumentation } from '@opentelemetry/instrumentation'
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction'
import type { WebTracerProvider } from '@opentelemetry/sdk-trace-web'

/** DOM events promoted to interaction spans (parity with the Kapitalvins SDK). */
export const USER_INTERACTION_EVENTS: (keyof HTMLElementEventMap)[] = [
  'click',
  'submit',
  'keydown',
  'change',
  'dblclick',
]

/**
 * Register the tracer provider with a {@link ZoneContextManager} and attach the
 * always-on base instrumentations plus user-interaction. Returns the disposer from
 * {@link registerInstrumentations} (the caller stores it for teardown). Must be
 * called at most once per provider — it performs the provider's single
 * `register()`.
 */
export function setupUserInteraction(
  provider: WebTracerProvider,
  baseInstrumentations: Instrumentation[],
): () => void {
  // ZoneContextManager replaces the default StackContextManager so trace context
  // survives across the interaction's async continuations.
  provider.register({ contextManager: new ZoneContextManager() })
  return registerInstrumentations({
    tracerProvider: provider,
    instrumentations: [
      ...baseInstrumentations,
      new UserInteractionInstrumentation({ eventNames: USER_INTERACTION_EVENTS }),
    ],
  })
}
